import { useMemo, useState } from "react";
import { useApp } from "../../../../../context/AppContext";
// @ts-ignore — ClientContext.js predates the TS migration
import { useClient } from "../../../../../context/ClientContext";
import { useServiceAdjustmentForms } from "../../../../../context/ServiceAdjustmentFormsContext";
import type { CalendarEventType } from "../../../../../types/calendar";
import type { Employee, SalonService } from "../../../../../types/salon";
import type { ServiceAdjustmentForm } from "../../../../../types/serviceAdjustmentForms";
import { getCreatedTransactions } from "../../../../../utils/bookingResponse";
import type { ServiceRow } from "../../booking/bookingTypes";
import {
  activeAdjustmentForms,
  adjustmentPriceDelta,
  appendRow,
  applySuggestedSlot,
  buildBookingPayload,
  isDraftBookable,
  nextRowUid,
  patchRowIn,
  performableServiceIds,
  removeRowFrom,
  selectServicePatch,
  selectStaffPatch,
  serviceIdsForRow,
  staffIdsForRow,
  suggestionQuery,
  toggleAdjustmentOption,
} from "../../booking/draftModel";
import { canAssignStaff, withinStaffLimit } from "../../booking/guards";
import type { BookingSession } from "../../booking/useBookingSession";
import {
  SuggestedSlot,
  useSuggestedTimes,
} from "../../booking/useSuggestedTimes";
import {
  ToastCopy,
  bookingToastCopy,
  draftTotals,
  rowConflict,
} from "./bookingSheetModel";

/** The booking-session surface the mobile sheet drives. */
export type MobileDraftSession = Pick<
  BookingSession,
  | "sheetOpen"
  | "rows"
  | "day"
  | "clientUuid"
  | "activeUid"
  | "discardPromptOpen"
  | "requestClose"
  | "close"
  | "dismissDiscard"
  | "handleCreated"
  | "handleRowsChange"
  | "handleClientChange"
  | "handleActiveChange"
>;

export interface ClientOption {
  id: string;
  name: string;
}

interface ClientRecord {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  area_code?: string;
  phone_number?: string;
}

export type DurationField =
  | "baseDuration"
  | "processingTime"
  | "finishingTime"
  | "bufferTime";

/** Rows shown in the client picker at once. */
const CLIENT_ROWS_MAX = 100;

const clientName = (c: ClientRecord | undefined): string =>
  c ? `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() : "";

const staffNameOf = (e: Employee | undefined): string =>
  e ? `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() : "";

/** Staff limit copy (not in design; see PR body gaps). */
export const STAFF_LIMIT_COPY = "An appointment can have at most 2 providers.";

interface UseMobileBookingDraftArgs {
  session: MobileDraftSession;
  services: SalonService[];
  /** Bookable active staff. */
  staff: Employee[];
  /** Calendar events (all staff) for conflicts. */
  events: CalendarEventType[];
  timezone: string;
}

/**
 * The mobile new-appointment draft: the shared booking session's rows /
 * client (bookingDraftReducer via handleRowsChange), edited with the same
 * pure helpers as the desktop form (booking/draftModel), plus derived
 * conflicts, suggested times and the create write.
 * The day is the draft's (the session owns it; CalendarContext.selectedDate
 * is only the viewed day).
 */
export function useMobileBookingDraft({
  session,
  services,
  staff,
  events,
  timezone,
}: UseMobileBookingDraftArgs) {
  const { createAppointment } = useApp();
  const { allClients } = (useClient() as { allClients?: ClientRecord[] }) || {};
  const { adjustmentForms, formJunctions } = useServiceAdjustmentForms();

  const { rows, day, clientUuid, activeUid, handleRowsChange } = session;
  const [submitting, setSubmitting] = useState(false);

  const serviceById = useMemo(() => {
    const map = new Map<string, SalonService>();
    services.forEach((s) => map.set(s.id, s));
    return map;
  }, [services]);
  const staffIds = useMemo(() => staff.map((e) => e.id), [staff]);
  const performable = useMemo(
    () => performableServiceIds(services, staffIds),
    [services, staffIds],
  );

  const formsFor = (salonServiceId: string): ServiceAdjustmentForm[] =>
    activeAdjustmentForms(adjustmentForms, formJunctions, salonServiceId);
  /** Shown price: base + adjustment delta (the backend adds the delta). */
  const priceOf = (r: ServiceRow): number =>
    (r.price || 0) + adjustmentPriceDelta(r, formsFor(r.salonServiceId));

  const bookable = isDraftBookable(rows, clientUuid, services);
  const client = (allClients || []).find((c) => c.id === clientUuid);
  const name = clientName(client);
  const totals = draftTotals(
    rows.filter((r) => r.salonServiceId),
    priceOf,
  );

  const staffName = (id: string): string =>
    staffNameOf(staff.find((e) => e.id === id));
  const serviceOf = (r: ServiceRow): SalonService | undefined =>
    serviceById.get(r.salonServiceId);

  // ── Conflicts ─────────────────────────────────────────────────────────
  const conflictOf = (r: ServiceRow): CalendarEventType | null =>
    rowConflict(events, r);
  const clashRows = rows.filter((r) => conflictOf(r));

  // ── Options ───────────────────────────────────────────────────────────
  const serviceOptions = (r: ServiceRow): SalonService[] =>
    serviceIdsForRow(
      r,
      services.map((s) => s.id),
      services,
      performable,
    )
      .map((id) => serviceById.get(id))
      .filter((s): s is SalonService => !!s);
  /** Providers offered for a row (qualified; within the 2-provider limit). */
  const staffOptions = (r: ServiceRow): Employee[] => {
    const ids = staffIdsForRow(r, staffIds, services).filter((id) =>
      canAssignStaff(rows, r.uid, id),
    );
    return staff.filter((e) => ids.includes(e.id));
  };

  const clientOptions = (query: string): ClientOption[] => {
    const q = query.trim().toLowerCase();
    const digits = q.replace(/\D/g, "");
    const all = (allClients || []).map((c) => ({ c, name: clientName(c) }));
    const hits = q
      ? all.filter(
          ({ c, name: n }) =>
            n.toLowerCase().includes(q) ||
            (c.email || "").toLowerCase().includes(q) ||
            (!!digits &&
              `${c.area_code ?? ""}${c.phone_number ?? ""}`.includes(digits)),
        )
      : all;
    return hits
      .slice(0, CLIENT_ROWS_MAX)
      .map(({ c, name: n }) => ({ id: c.id, name: n }));
  };

  // ── Edits (shared reducer via the session) ────────────────────────────
  const update = (
    uid: number,
    patchFor: (row: ServiceRow) => Partial<ServiceRow>,
  ) => handleRowsChange((prev) => patchRowIn(prev, uid, patchFor));

  const setService = (uid: number, salonServiceId: string) =>
    update(uid, (row) =>
      selectServicePatch(row, salonServiceId, serviceById.get(salonServiceId)),
    );
  const setStaff = (uid: number, employeeId: string) =>
    update(uid, (row) =>
      selectStaffPatch(row, employeeId, serviceById.get(row.salonServiceId)),
    );
  /** The first service's date; every row follows it (single day). */
  const setDate = (uid: number, date: string) => {
    if (date) update(uid, () => ({ date }));
  };
  const setTime = (uid: number, time: string) => {
    if (time) update(uid, () => ({ time }));
  };
  const setDuration = (uid: number, field: DurationField, value: number) =>
    update(uid, () => ({ [field]: value }) as Partial<ServiceRow>);
  const toggleAdjustment = (
    uid: number,
    optionId: string,
    questionId: string,
  ) =>
    handleRowsChange((prev) =>
      prev.map((r) =>
        r.uid === uid
          ? toggleAdjustmentOption(
              r,
              formsFor(r.salonServiceId),
              optionId,
              questionId,
            )
          : r,
      ),
    );
  const addRow = () => {
    const uid = nextRowUid(rows);
    handleRowsChange((prev) => appendRow(prev, uid, day));
    session.handleActiveChange(uid);
  };
  const removeRow = (uid: number) =>
    handleRowsChange((prev) => removeRowFrom(prev, uid));

  // ── Suggested times (desktop's rule: draftModel.suggestionQuery) ─────
  // Every chosen service in order, providers aligned, first row's time as the
  // requested start.
  const sq = suggestionQuery(rows);
  const sugg = useSuggestedTimes({
    serviceIds: sq.serviceIds,
    staffIds: sq.staffIds,
    startTime: sq.startTime,
    date: sq.date,
    timezone,
  });
  const applySuggestion = (slot: SuggestedSlot) => {
    if (sq.serviceIds.length === 0) return;
    handleRowsChange((prev) =>
      applySuggestedSlot(prev, sq, slot, timezone, services),
    );
  };

  // ── Review + write ────────────────────────────────────────────────────
  /**
   * Check the provider limit (a calendar move can add a provider). Returns an
   * error message, or null to go to review.
   */
  const prepareReview = (): string | null => {
    if (!bookable) return "";
    if (!withinStaffLimit(rows)) return STAFF_LIMIT_COPY;
    return null;
  };

  /** POST /appointments (desktop's create payload); the toast copy on success. */
  const submit = async (): Promise<
    { toast: ToastCopy } | { error: string }
  > => {
    if (!bookable || submitting) return { error: "" };
    setSubmitting(true);
    try {
      const payload = buildBookingPayload({
        rows,
        clientUuid,
        mode: "create",
        timezone,
        services,
      });
      const response = await createAppointment(payload);
      const toast = bookingToastCopy(rows.length, name, totals);
      session.handleCreated(getCreatedTransactions(response));
      return { toast };
    } catch (e) {
      return {
        error:
          (e instanceof Error && e.message) ||
          "Could not create the appointment.",
      };
    } finally {
      setSubmitting(false);
    }
  };

  return {
    rows,
    day,
    clientUuid,
    clientName: name,
    activeUid,
    bookable,
    totals,
    submitting,
    serviceOf,
    staffName,
    priceOf,
    formsFor,
    conflictOf,
    clashRows,
    serviceOptions,
    staffOptions,
    clientOptions,
    setClient: session.handleClientChange,
    setActive: session.handleActiveChange,
    setService,
    setStaff,
    setDate,
    setTime,
    setDuration,
    toggleAdjustment,
    addRow,
    removeRow,
    /** True when suggested times are asked for (a service is chosen). */
    hasSuggestionQuery: sq.serviceIds.length > 0,
    suggestions: sugg.suggestions,
    suggestionsLoading: sugg.loading,
    suggestionsError: sugg.error,
    applySuggestion,
    prepareReview,
    submit,
  };
}

export type MobileBookingDraft = ReturnType<typeof useMobileBookingDraft>;
